// builderValidation — the Save-time gate for the builder canvas. Pure: it walks
// the builder MODEL (BuilderPage[]) and returns issues; the page decides what to
// do with them. Validating the model rather than buildElementsPayload's output is
// deliberate — the payload JSON-stringifies `properties` and strips the client
// `id`, so the payload can neither be read cheaply nor mapped back to a card.
//
// Element rules come in two registries: COMMON_RULES apply to every element,
// TYPE_RULES layers extra checks onto one elementType. Beside them sit STRUCTURAL
// rules, which can't be element rules because they need a container's field list
// (a page or section must not be empty) or a form-wide count (exactly one
// companyStructure field) rather than one element's properties.
//
// Rules report i18n KEYS, never translated strings (the auth-pages/validation.ts
// convention) — describeIssues does the translating. A rule's optional `labelKey`
// is the PROPERTIES PANEL's own label for the field, interpolated as {{label}},
// so one generic "X is required" message covers many fields and always words them
// the way the panel does.

import {
  basicKeyPrefix,
  basicRecord,
  type ChoiceOption,
  type ElementProperties,
  type ElementType,
  type FieldImageRef,
  type LibraryElementType,
} from './formElementDefaults';
import type { BuilderPage } from './builderModel';
import { deriveCanvasItems, type CanvasFieldItem } from './canvasItems';
import { deriveGotoMap } from './triggerOps';

/** The `otp-code` mention's MACHINE value, as listed in FormBuilderBasicExtras'
 *  OTP_MENTIONS. Named because requiredOtpCode below matches on it and a bare
 *  literal in two files would drift. */
const OTP_CODE_MENTION = 'otp-code';

/** Enough of an element for the rules — BuilderPage and BuilderField both fit. */
type ValidatableElement = { elementType: ElementType; properties: ElementProperties };

/** What a rule reports: the message key, plus the label to interpolate into it. */
type Problem = { messageKey: string; labelKey?: string };

/** A rule returns the problem it found, or null when it passes. */
type ElementRule = (el: ValidatableElement) => Problem | null;

export interface BuilderIssue {
  /** Page the offending element sits on — a "jump to it" affordance needs this.
   *  null for a form-wide problem, which belongs to no page. */
  pageId: string | null;
  /** 1-based, as the canvas page pills number them; null when form-wide. */
  pageNumber: number | null;
  /** Client id of the element; null when the PAGE or the FORM is the problem. */
  elementId: string | null;
  /** Canvas position label — '3', '5.2'. Follow-up rows carry no number of
   *  their own, so a 'follow_up' issue names its HOST card's badge. null for
   *  page- or form-level issues. */
  badge: string | null;
  /** Picks the locator sentence in describeIssues. */
  scope: 'form' | 'page' | 'section' | 'field' | 'follow_up';
  messageKey: string;
  /** Label key to interpolate as {{label}}; absent when the message stands alone.
   *  These reuse the properties panel's OWN label keys, so a message names the
   *  field exactly as the panel labels it. */
  labelKey?: string;
}

/** The element's title, whatever its prefix (fieldTitle / sectionTitle / pageTitle). */
const titleOf = (el: ValidatableElement) =>
  String(basicRecord(el.properties)[`${basicKeyPrefix(el.elementType)}Title`] ?? '');

// Every type carries a title key and DEFAULTS IT TO '' (never undefined), and the
// properties panel writes the raw input value — so this has to be a trim check: an
// undefined test would never fire, and plain truthiness would pass "   ".
// `image` is a display-only element, so it is exempt.
const titleRequired: ElementRule = (el) =>
  el.elementType === 'image' || titleOf(el).trim()
    ? null
    : { messageKey: 'forms_builder_problem_title_required' };

/** Applied to every element, whatever its type. */
const COMMON_RULES: ElementRule[] = [titleRequired];

// ---- per-type rule factories ----
// Everything these read lives in basicProperties. They go through basicRecord
// rather than narrowing per elementType: the properties union isn't discriminated
// by elementType, so narrowing would be noise.

const basicText = (el: ValidatableElement, key: string) =>
  String(basicRecord(el.properties)[key] ?? '').trim();

const basicList = (el: ValidatableElement, key: string): unknown[] => {
  const value = basicRecord(el.properties)[key];
  return Array.isArray(value) ? value : [];
};

const requiredText =
  (key: string, labelKey: string): ElementRule =>
  (el) =>
    basicText(el, key) ? null : { messageKey: 'forms_builder_problem_required', labelKey };

const requiredList =
  (key: string, labelKey: string): ElementRule =>
  (el) =>
    basicList(el, key).length ? null : { messageKey: 'forms_builder_problem_min_one', labelKey };

// At least one option carrying actual text. Options are added PRE-LABELLED
// ('Option' — emptyChoiceOption), so in practice this catches a label the user
// cleared, not only a list they never filled.
const requiredChoices: ElementRule = (el) =>
  basicList(el, 'optionList').some((o) => String((o as ChoiceOption).responseText ?? '').trim())
    ? null
    : { messageKey: 'forms_builder_problem_min_one', labelKey: 'forms_builder_props_options' };

// otpMessageContentMap is display -> machine value, so match on the VALUE, which
// is the stable half. OTP_MENTIONS now uses literal displays, so NEW data keys on
// 'OTP' — but forms authored earlier under Arabic carry '{رمز التحقق: otp-code}',
// and MentionTextarea deliberately preserves the mapping of a token whose display
// is no longer in `options`. Keying on 'OTP' would reject exactly those forms.
// The map is derived from the message text on each edit, so this is equivalent to
// "the message still contains the OTP token".
const requiredOtpCode: ElementRule = (el) => {
  const map = basicRecord(el.properties).otpMessageContentMap;
  const values = map && typeof map === 'object' ? Object.values(map as Record<string, string>) : [];
  return values.includes(OTP_CODE_MENTION)
    ? null
    : { messageKey: 'forms_builder_problem_otp_code_required' };
};

// A PENDING pick counts: validation runs before the upload, so an unsaved File is
// as good as a stored descriptor. FieldImageRef has every field optional (and the
// payload sanitiser can emit `id: undefined`), so a non-null ref is NOT proof of an
// image — check `id`. fieldImageFile is typed `unknown`; the codebase narrows it
// with `instanceof File`.
const requiredImage: ElementRule = (el) => {
  const bp = basicRecord(el.properties);
  const ref = bp.fieldImage as FieldImageRef | null;
  return ref?.id || bp.fieldImageFile instanceof File
    ? null
    : { messageKey: 'forms_builder_problem_required', labelKey: 'forms_builder_props_basic_image' };
};

/** Layered on top of the common rules for one elementType. Label keys are the
 *  properties panel's own, so a message names the field as the panel labels it. */
const TYPE_RULES: Partial<Record<LibraryElementType, ElementRule[]>> = {
  checkList: [requiredChoices],
  ratingSlider: [
    requiredText('lowLabel', 'forms_builder_props_low_label'),
    requiredText('highLabel', 'forms_builder_props_high_label'),
  ],
  dataSource: [
    requiredText('dataSource', 'forms_builder_ds_source'),
    requiredText('displayColumn', 'forms_builder_ds_display_column'),
    requiredText('valueColumn', 'forms_builder_ds_value_column'),
  ],
  companyField: [
    requiredText('companyFieldId', 'forms_builder_props_cf_field'),
    requiredList('companyFieldDisplayColumns', 'forms_builder_props_cf_display_columns'),
  ],
  otp: [requiredText('otpMessageContent', 'forms_builder_props_otp_message'), requiredOtpCode],
  image: [requiredImage],
};

// 'page' is not a LibraryElementType, so pages get the common rules only.
const rulesFor = (type: ElementType): ElementRule[] => [
  ...COMMON_RULES,
  ...(TYPE_RULES[type as LibraryElementType] ?? []),
];

const runRules = (el: ValidatableElement): Problem[] =>
  rulesFor(el.elementType)
    .map((rule) => rule(el))
    .filter((problem): problem is Problem => problem !== null);

// ---- structural rules ----
// These can't be ElementRules: they need a container's field list or a form-wide
// count, not one element's properties.

/** A page, and a section, must each hold at least one field. Shared because the
 *  message is the same — only the locator differs. */
const needsField = (count: number): Problem[] =>
  count === 0 ? [{ messageKey: 'forms_builder_problem_needs_field' }] : [];

/** companyStructure must not sit on a page any go-to rule jumps to: flows that
 *  don't take the jump would skip the form's exactly-once required field. */
const gotoHostsCompanyStructure = (onTargetedPage: boolean, el: ValidatableElement): Problem[] =>
  onTargetedPage && el.elementType === 'companyStructure'
    ? [{ messageKey: 'forms_builder_problem_company_structure_goto' }]
    : [];

export const isCompanyStructure = (el: { elementType: string }) =>
  el.elementType === 'companyStructure';

/** companyStructure fields across every page — over the flat `fields` list, so
 *  one nested inside a section still counts. */
export const countCompanyStructure = (pages: BuilderPage[]) =>
  pages.reduce((n, page) => n + page.fields.filter(isCompanyStructure).length, 0);

/** Exactly one companyStructure field per FORM. The builder already keeps a
 *  second one out; this still catches a saved design that loads with two. */
const formProblems = (pages: BuilderPage[]): Problem[] => {
  const count = countCompanyStructure(pages);
  if (count === 0) return [{ messageKey: 'forms_builder_problem_company_structure_missing' }];
  if (count > 1) return [{ messageKey: 'forms_builder_problem_company_structure_duplicate' }];
  return [];
};

/** Every problem on the canvas, in the order the elements render — so issues[0]
 *  is deterministically the topmost one. */
export const validateBuilderPages = (pages: BuilderPage[]): BuilderIssue[] => {
  const issues: BuilderIssue[] = [];

  // Pages any go-to rule jumps to — same derivation as the page picker's
  // guards (deriveGotoMap), so validation and picker agree on "targeted".
  const gotoTargets = new Set<string>();
  for (const targets of deriveGotoMap(pages).values())
    for (const id of targets) gotoTargets.add(id);

  pages.forEach((page, pageIndex) => {
    const pageNumber = pageIndex + 1;
    const at = (
      scope: BuilderIssue['scope'],
      elementId: string | null,
      badge: string | null,
      problems: Problem[],
    ) => {
      for (const problem of problems)
        issues.push({ pageId: page.id, pageNumber, elementId, badge, scope, ...problem });
    };

    // The page itself first — pages aren't in `fields`, so they're checked apart.
    at('page', null, null, [...runRules(page), ...needsField(page.fields.length)]);

    // Conditional-logic follow-ups have no number of their own — every issue
    // on one (any depth) names the badge of the card that hosts it, which is
    // where the user goes to see the row.
    const visitFollowUps = (host: CanvasFieldItem, badge: string) => {
      for (const followUp of host.followUps) {
        at('follow_up', followUp.field.id, badge, runRules(followUp.field));
        visitFollowUps(followUp, badge);
      }
    };

    // Badges come from the same derivation the canvas renders from, so a message
    // names the position the user actually sees on the card ('2', '5.2').
    deriveCanvasItems(page.fields).forEach((item, pos) => {
      if (item.kind === 'section') {
        at('section', item.section.id, `${pos + 1}`, [
          ...runRules(item.section),
          ...needsField(item.children.length),
        ]);
        item.children.forEach((child, k) => {
          at('field', child.field.id, `${pos + 1}.${k + 1}`, [
            ...runRules(child.field),
            ...gotoHostsCompanyStructure(gotoTargets.has(page.id), child.field),
          ]);
          visitFollowUps(child, `${pos + 1}.${k + 1}`);
        });
        return;
      }
      at('field', item.field.id, `${pos + 1}`, [
        ...runRules(item.field),
        ...gotoHostsCompanyStructure(gotoTargets.has(page.id), item.field),
      ]);
      visitFollowUps(item, `${pos + 1}`);
    });
  });

  // Form-wide problems go LAST so the toast leads with something locatable — a
  // "go to page 2, field 3" beats an unplaced statement when both exist.
  for (const problem of formProblems(pages))
    issues.push({
      pageId: null,
      pageNumber: null,
      elementId: null,
      badge: null,
      scope: 'form',
      ...problem,
    });

  return issues;
};

/** Toast copy: the total count plus the topmost problem. Composed through two
 *  keys (a locator sentence + the problem) rather than concatenated fragments, so
 *  each stays naturally translatable. */
export const describeIssues = (
  issues: BuilderIssue[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const [first] = issues;
  // The 'form' locator sentence carries no page/badge — it renders the problem
  // alone — so the empty fallbacks below are simply unused there.
  const where = t(`forms_builder_issue_at_${first.scope}`, {
    page: first.pageNumber ?? '',
    badge: first.badge ?? '',
    // The label is itself a key — the properties panel's own label for the field.
    problem: t(first.messageKey, first.labelKey ? { label: t(first.labelKey) } : undefined),
  });
  return t('forms_builder_save_blocked', { count: issues.length, first: where });
};
